export interface CORAInput {
    nodeIds?: string[];
    nodeId?: string;
    country?: string;
    durationOfRiskWindowInDays: number;
    modeSelector: string;
    fulfillmentProgram: string;
}

export interface GetCORADataRequest {
    nodeIds: string[];
    fulfillmentProgram: string;
    modeSelector: string;
    durationOfRiskWindowInDays: number;
}

export interface GetCORADataResponse {
    riskDataByNode: { [nodeId: string]: CORARiskDataByNode };
    nodeFailures?: { [nodeId: string]: NodeFailureDetails };
}

export interface CORADetailedResults {
    riskDataByNode: CORARiskDataByNode[];
    aggregatedData?: CORAggregatedData;
    metadata?: CORAMetadata;
}

export interface CORARiskDataByNode {
    nodeId: string;
    nodeName?: string;
    evaluationTime?: string;
    featureConfigurations?: string[];
    latestDataIngestionTime?: string;
    minSLA?: number;
    nodeTimeZone?: string;
    riskDataPerEXSDByPPG?: { [ppg: string]: CORARiskDataPerEXSD[] };
    slamPadTimeByPPG?: { [ppg: string]: number };
    riskDataPerEXSD?: CORARiskDataPerEXSD[];
    processPathFamilyData?: CORAProcessPathFamilyData[];
    compositeFunctionData?: CORACompositeFunctionData[];
    operationalConfig?: CORAOperationalConfig;
    featureConfig?: CORAFeatureConfig;
}

export interface CORARiskDataPerEXSD {
    exsd?: string;
    cumulativePlannedUnitsAsPercentOfCumulativeShipCapacity?: number;
    cumulativePlannedUnitsDueOutForCPT?: number;
    cumulativeProjectedUnitsAsPercentOfCumulativeShipCapacity?: number;
    cumulativeProjectedUnitsDueOutUntilCPT?: number;
    cumulativeShipCapacityUntilCPT?: number;
    expectedNewDropsForCPT?: number;
    remainingUnitsDueOutForCPT?: number;
    totalProjectedUnitsDueOutForCPT?: number;
    totalUnitsWithoutNewDropsForCPT?: number;
    unitsPlannedForCPT?: number;
    
    // Legacy frontend field names (for backward compatibility)
    externalShipmentDate?: string;
    utilizationPercentage?: number;
    capacityUtilized?: number;
    totalCapacity?: number;
    riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    riskScore?: number;
    shipmentVolume?: number;
    processPathFamily?: string;
    compositeFunction?: string;
    remainingUnits?: number;
    plannedUnits?: number;
    expectedNewCharge?: number;
    cumulativeUnitsWithoutNewCharge?: number;
    cumulativeProjectedUnits?: number;
    cumulativeShipCapacity?: number;
    projectedUtilizationPercentage?: number;
}

export interface CORAProcessPathFamilyData {
    processPathFamily: string;
    utilizationData: CORARiskDataPerEXSD[];
    r2fMetadata?: CORAR2FMetadata;
}

export interface CORACompositeFunctionData {
    compositeFunction: string;
    utilizationData: CORARiskDataPerEXSD[];
}

export interface CORAR2FMetadata {
    processPathFamily: string;
    readyToFulfillTime: number;
    slaThreshold: number;
    currentPerformance: number;
}

export interface CORAOperationalConfig {
    nodeId: string;
    slaConfiguration: {
        [key: string]: number;
    };
    slamPadTimes: {
        [key: string]: number;
    };
}

export interface CORAFeatureConfig {
    nodeId: string;
    enabledFeatures: string[];
    featureSettings: {
        [key: string]: any;
    };
}

export interface CORAggregatedData {
    totalNodes: number;
    averageUtilization: number;
    highRiskNodes: string[];
    criticalRiskNodes: string[];
}

export interface CORAMetadata {
    queryTimeRange: {
        beginInstant: string;
        endInstant: string;
    };
    fulfillmentProgram: string;
    modeSelector: string;
    generatedAt: string;
}

// Mode selector options
export const MODE_SELECTORS = {
    EXSD: 'exsd',
    PROCESS_PATH_FAMILY: 'ppf'
} as const;

export type ModeSelector = typeof MODE_SELECTORS[keyof typeof MODE_SELECTORS];

export const FULFILLMENT_PROGRAMS = {
    AMAZON_FULFILLMENT: 'AMAZON_FULFILLMENT',
    DIRECT_FULFILLMENT: 'DIRECT_FULFILLMENT',
    EF_FORWARD_DEPLOY: 'EF_FORWARD_DEPLOY',
    EF_HEAVY_BULKY: 'EF_HEAVY_BULKY',
    EF_MINI_FC: 'EF_MINI_FC',
    EF_MULTI_SELLER_FLEX: 'EF_MULTI_SELLER_FLEX',
    EF_SELLER_FLEX: 'EF_SELLER_FLEX',
    EF_SUB_SAME_DAY: 'EF_SUB_SAME_DAY',
    EF_THIRD_PARTY_LOGISTICS: 'EF_THIRD_PARTY_LOGISTICS',
    EF_VENDOR_FLEX: 'EF_VENDOR_FLEX',
    EF_SMART_CONNECT: 'EF_SMART_CONNECT'
} as const;

export type FulfillmentProgram = typeof FULFILLMENT_PROGRAMS[keyof typeof FULFILLMENT_PROGRAMS];

export enum IssueType {
    BROKEN_CONFIGURATION = 'BROKEN_CONFIGURATION',
    CORA_INTERNAL_ISSUE = 'CORA_INTERNAL_ISSUE',
    UNAVAILABLE_DATA = 'UNAVAILABLE_DATA',
    UNAVAILABLE_FEATURE = 'UNAVAILABLE_FEATURE',
    UNPROCESSABLE_DIMENSIONS = 'UNPROCESSABLE_DIMENSIONS',
    UNREGISTERED_NODE = 'UNREGISTERED_NODE',
    DEPRECATED_FEATURE = 'DEPRECATED_FEATURE'
}

export interface NodeFailureDetails {
    failureMessage: string;
    shouldRetry: boolean;
    issueType: IssueType;
}

export const RISK_LEVELS = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CRITICAL: 'CRITICAL'
} as const;

export type RiskLevel = typeof RISK_LEVELS[keyof typeof RISK_LEVELS];

export type WarehouseIdList = string[];
export type RiskDataByNodeMap = { [nodeId: string]: CORARiskDataByNode };
export type NodeFailuresMap = { [nodeId: string]: NodeFailureDetails };
